/**
 * Patient Rapporter: selection refs and user-written text.
 * Source content (patient, journal, markings, questionnaires) is read live
 * for preview and PDF. It is not stored on the report.
 * The finance reports module is a different feature and is not used here.
 */

export const PATIENT_REPORT_SOURCE_KINDS = [
  'patient_card',
  'journal_note',
  'marking',
  'questionnaire',
  'file',
] as const;

export type PatientReportSourceKind = (typeof PATIENT_REPORT_SOURCE_KINDS)[number];

export type PatientReportBlockKind = PatientReportSourceKind | 'text';

export type PatientReportBlock = {
  id: string;
  kind: PatientReportBlockKind;
  sourceId: string | null;
  heading: string;
  body: string;
};

export type PatientReportSourceRef = {
  kind: PatientReportSourceKind;
  sourceId: string;
  label: string;
};

export type PatientReportLetterheadMode = 'none' | 'invoice' | 'custom';

/** `inherit` uses the clinic default from Integrationer. */
export type PatientReportLetterheadChoice = 'inherit' | PatientReportLetterheadMode;

export type PatientReportSettings = {
  enabled: boolean;
  letterhead: PatientReportLetterheadMode;
  letterheadStoragePath: string | null;
  letterheadMime: string | null;
};

export type PatientReportRow = {
  id: string;
  title: string;
  blocks: PatientReportBlock[];
  sources: PatientReportSourceRef[];
  /** Raw selection JSON. Parsed by parseReportSections. Not patient content. */
  composition: unknown[];
  templateId: string | null;
  letterhead: PatientReportLetterheadChoice | null;
  includeCover: boolean;
  coverTitle: string;
  coverBody: string;
  status: 'draft' | 'finished';
  pdfStoragePath: string | null;
  finishedAt: string | null;
  updatedAt: string;
  createdAt: string;
};

export type PatientCardSource = {
  id: string;
  patNumber: string | null;
  name: string;
  jobTitle: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  notes: string | null;
  cave: string | null;
  /** Latest referral diagnosis, read live. Never written onto a report row. */
  diagnosis: string | null;
  preferredLocale: string | null;
};

export type JournalNoteSource = {
  id: string;
  entryDate: string;
  title: string | null;
  body: string;
};

export type MarkingSource = {
  id: string;
  quote: string;
  comment: string | null;
  filename: string;
  createdAt: string | null;
  prefix: string | null;
  suffix: string | null;
  page: number | null;
  tagIds: string[];
};

export type QuestionnaireSource = {
  id: string;
  formsAssignmentId: string | null;
  formsTemplateId: string | null;
  name: string;
  status: string;
  completedAt: string | null;
  scoreText: string | null;
};

export type FileSource = {
  id: string;
  filename: string;
  mimeType: string;
  notes: string | null;
};

export type HighlightTagSource = {
  id: string;
  name: string;
};

export type PatientReportCatalog = {
  patient: PatientCardSource | null;
  journalNotes: JournalNoteSource[];
  markings: MarkingSource[];
  questionnaires: QuestionnaireSource[];
  files: FileSource[];
  highlightTags: HighlightTagSource[];
};

export const EMPTY_REPORT_CATALOG: PatientReportCatalog = {
  patient: null,
  journalNotes: [],
  markings: [],
  questionnaires: [],
  files: [],
  highlightTags: [],
};

function text(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function record(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function arrayOf(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

export function parsePatientReportSettings(value: unknown): PatientReportSettings {
  const row = record(value);
  const letterhead = row?.letterhead;
  const mode: PatientReportLetterheadMode =
    letterhead === 'invoice' || letterhead === 'custom' ? letterhead : 'none';
  return {
    enabled: row?.enabled === true,
    letterhead: mode,
    letterheadStoragePath: text(row?.letterhead_storage_path),
    letterheadMime: text(row?.letterhead_mime),
  };
}

function parseBlock(value: unknown): PatientReportBlock | null {
  const row = record(value);
  const id = text(row?.id);
  const kind = row?.kind;
  if (!id || typeof kind !== 'string') return null;
  if (kind !== 'text' && !(PATIENT_REPORT_SOURCE_KINDS as readonly string[]).includes(kind)) {
    return null;
  }
  return {
    id,
    kind: kind as PatientReportBlockKind,
    sourceId: text(row?.sourceId),
    heading: typeof row?.heading === 'string' ? row.heading.slice(0, 300) : '',
    body: typeof row?.body === 'string' ? row.body.slice(0, 20000) : '',
  };
}

function parseSourceRef(value: unknown): PatientReportSourceRef | null {
  const row = record(value);
  const sourceId = text(row?.sourceId);
  const kind = row?.kind;
  const label = text(row?.label) ?? '';
  if (!sourceId || typeof kind !== 'string') return null;
  if (!(PATIENT_REPORT_SOURCE_KINDS as readonly string[]).includes(kind)) return null;
  return { kind: kind as PatientReportSourceKind, sourceId, label: label.slice(0, 300) };
}

export function parsePatientReportRow(value: unknown): PatientReportRow | null {
  const row = record(value);
  const id = text(row?.id);
  const title = text(row?.title);
  if (!id || !title) return null;
  const status = row?.status === 'finished' ? 'finished' : 'draft';
  const letterheadRaw = row?.letterhead;
  const letterhead: PatientReportLetterheadChoice | null =
    letterheadRaw === 'inherit' ||
    letterheadRaw === 'none' ||
    letterheadRaw === 'invoice' ||
    letterheadRaw === 'custom'
      ? letterheadRaw
      : null;
  return {
    id,
    title: title.slice(0, 200),
    blocks: arrayOf(row?.blocks)
      .map(parseBlock)
      .filter((block): block is PatientReportBlock => Boolean(block)),
    sources: arrayOf(row?.sources)
      .map(parseSourceRef)
      .filter((source): source is PatientReportSourceRef => Boolean(source)),
    composition: arrayOf(row?.composition),
    templateId: text(row?.template_id),
    letterhead,
    includeCover: row?.include_cover === true,
    coverTitle: typeof row?.cover_title === 'string' ? row.cover_title.slice(0, 300) : '',
    coverBody: typeof row?.cover_body === 'string' ? row.cover_body.slice(0, 4000) : '',
    status,
    pdfStoragePath: text(row?.pdf_storage_path),
    finishedAt: text(row?.finished_at),
    updatedAt: text(row?.updated_at) ?? '',
    createdAt: text(row?.created_at) ?? '',
  };
}

export function parsePatientReportList(value: unknown): PatientReportRow[] {
  return arrayOf(value)
    .map(parsePatientReportRow)
    .filter((row): row is PatientReportRow => Boolean(row));
}

export function parsePatientReportCatalog(value: unknown): PatientReportCatalog {
  const row = record(value);
  const patientRow = record(row?.patient);
  const patientId = text(patientRow?.id);
  const patient: PatientCardSource | null =
    patientRow && patientId
      ? {
          id: patientId,
          patNumber: text(patientRow.pat_number),
          name: text(patientRow.name) ?? '',
          jobTitle: text(patientRow.job_title),
          email: text(patientRow.email),
          phone: text(patientRow.phone),
          address: text(patientRow.address),
          notes: text(patientRow.notes),
          cave: text(patientRow.cave),
          diagnosis: text(patientRow.diagnosis),
          preferredLocale: text(patientRow.preferred_locale),
        }
      : null;

  const journalNotes = arrayOf(row?.journal_notes).flatMap((item) => {
    const note = record(item);
    const id = text(note?.id);
    if (!note || !id) return [];
    return [
      {
        id,
        entryDate: text(note.entry_date) ?? '',
        title: text(note.title),
        body: typeof note.body === 'string' ? note.body : '',
      },
    ];
  });

  const markings = arrayOf(row?.markings).flatMap((item) => {
    const marking = record(item);
    const id = text(marking?.id);
    const quote = text(marking?.quote);
    if (!marking || !id || !quote) return [];
    return [
      {
        id,
        quote,
        comment: text(marking.comment),
        filename: text(marking.filename) ?? '',
        createdAt: text(marking.created_at),
        prefix: text(marking.prefix),
        suffix: text(marking.suffix),
        page:
          typeof marking.page === 'number' && Number.isFinite(marking.page) ? marking.page : null,
        tagIds: arrayOf(marking.tag_ids).flatMap((tagId) => {
          const id = text(tagId);
          return id ? [id] : [];
        }),
      },
    ];
  });

  const questionnaires = arrayOf(row?.questionnaires).flatMap((item) => {
    const form = record(item);
    const id = text(form?.id);
    const name = text(form?.name);
    if (!form || !id || !name) return [];
    return [
      {
        id,
        formsAssignmentId: text(form.forms_assignment_id),
        formsTemplateId: text(form.forms_template_id),
        name,
        status: text(form.status) ?? '',
        completedAt: text(form.completed_at),
        scoreText: text(form.score_text),
      },
    ];
  });

  const files = arrayOf(row?.files).flatMap((item) => {
    const file = record(item);
    const id = text(file?.id);
    const filename = text(file?.filename);
    if (!file || !id || !filename) return [];
    return [
      {
        id,
        filename,
        mimeType: text(file.mime_type) ?? '',
        notes: text(file.notes),
      },
    ];
  });

  const highlightTags = arrayOf(row?.highlight_tags).flatMap((item) => {
    const tag = record(item);
    const id = text(tag?.id);
    const name = text(tag?.name);
    if (!tag || !id || !name) return [];
    return [{ id, name }];
  });

  return { patient, journalNotes, markings, questionnaires, files, highlightTags };
}

export function sourcesFromBlocks(blocks: readonly PatientReportBlock[]): PatientReportSourceRef[] {
  const seen = new Set<string>();
  const sources: PatientReportSourceRef[] = [];
  for (const block of blocks) {
    if (block.kind === 'text' || !block.sourceId) continue;
    const key = `${block.kind}:${block.sourceId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    sources.push({
      kind: block.kind,
      sourceId: block.sourceId,
      label: (block.heading || block.kind).slice(0, 300),
    });
  }
  return sources;
}

export function blockIsSelected(
  blocks: readonly PatientReportBlock[],
  kind: PatientReportSourceKind,
  sourceId: string,
): boolean {
  return blocks.some((block) => block.kind === kind && block.sourceId === sourceId);
}

export function newReportBlockId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `block-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function insertReportBlock(
  blocks: readonly PatientReportBlock[],
  block: PatientReportBlock,
): PatientReportBlock[] {
  if (
    block.kind !== 'text' &&
    block.sourceId &&
    blockIsSelected(blocks, block.kind, block.sourceId)
  ) {
    return [...blocks];
  }
  return [...blocks, block];
}

export function removeReportSource(
  blocks: readonly PatientReportBlock[],
  kind: PatientReportSourceKind,
  sourceId: string,
): PatientReportBlock[] {
  return blocks.filter((block) => !(block.kind === kind && block.sourceId === sourceId));
}

export function moveReportBlock(
  blocks: readonly PatientReportBlock[],
  id: string,
  direction: -1 | 1,
): PatientReportBlock[] {
  const index = blocks.findIndex((block) => block.id === id);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= blocks.length) return [...blocks];
  const next = [...blocks];
  const [item] = next.splice(index, 1);
  next.splice(target, 0, item);
  return next;
}

/** dnd-kit arrayMove: the dragged block lands on the block it was dropped on. */
export function reorderReportBlocks(
  blocks: readonly PatientReportBlock[],
  activeId: string,
  overId: string,
): PatientReportBlock[] {
  const from = blocks.findIndex((block) => block.id === activeId);
  const to = blocks.findIndex((block) => block.id === overId);
  if (from < 0 || to < 0 || from === to) return [...blocks];
  const next = [...blocks];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

export function updateReportBlock(
  blocks: readonly PatientReportBlock[],
  id: string,
  patch: Partial<Pick<PatientReportBlock, 'heading' | 'body'>>,
): PatientReportBlock[] {
  return blocks.map((block) => (block.id === id ? { ...block, ...patch } : block));
}

export type ReportFieldLabels = {
  patientCard: string;
  patNumber: string;
  name: string;
  jobTitle: string;
  email: string;
  phone: string;
  address: string;
  notes: string;
  cave: string;
  diagnosis: string;
  locale: string;
  journal: string;
  marking: string;
  quote: string;
  comment: string;
  file: string;
  questionnaire: string;
  status: string;
  score: string;
  answer: string;
  untitled: string;
};

function line(label: string, value: string | null): string | null {
  if (!value) return null;
  return `${label}: ${value}`;
}

export function patientCardBody(patient: PatientCardSource, labels: ReportFieldLabels): string {
  return [
    line(labels.name, patient.name),
    line(labels.patNumber, patient.patNumber),
    line(labels.jobTitle, patient.jobTitle),
    line(labels.email, patient.email),
    line(labels.phone, patient.phone),
    line(labels.address, patient.address),
    line(labels.locale, patient.preferredLocale),
    line(labels.cave, patient.cave),
    line(labels.diagnosis, patient.diagnosis),
    line(labels.notes, patient.notes),
  ]
    .filter((part): part is string => Boolean(part))
    .join('\n');
}

export function journalNoteHeading(note: JournalNoteSource, labels: ReportFieldLabels): string {
  const title = note.title ?? labels.untitled;
  return note.entryDate
    ? `${labels.journal} ${note.entryDate} — ${title}`
    : `${labels.journal} — ${title}`;
}

export function markingBody(marking: MarkingSource, labels: ReportFieldLabels): string {
  return [
    line(labels.file, marking.filename),
    line(labels.quote, marking.quote),
    line(labels.comment, marking.comment),
  ]
    .filter((part): part is string => Boolean(part))
    .join('\n');
}

export function questionnaireBody(
  form: QuestionnaireSource,
  labels: ReportFieldLabels,
  answers?: string | null,
): string {
  return [
    line(labels.status, form.status),
    line(labels.score, form.scoreText),
    answers?.trim() ? `${labels.answer}:\n${answers.trim()}` : null,
  ]
    .filter((part): part is string => Boolean(part))
    .join('\n');
}

export function fileBody(file: FileSource, labels: ReportFieldLabels): string {
  return [
    line(labels.file, file.filename),
    file.mimeType ? file.mimeType : null,
    line(labels.notes, file.notes),
  ]
    .filter((part): part is string => Boolean(part))
    .join('\n');
}

export type FormsAnswerQuestion = {
  id: string;
  text?: string;
  text_da?: string;
  options?: Array<{ value: string; label?: string; label_da?: string }>;
};

/** Plain-text answers from a Forms assignment detail. Empty when nothing was answered. */
export function formatFormsAnswers(input: {
  questions: readonly FormsAnswerQuestion[] | null | undefined;
  responses: Record<string, unknown> | null | undefined;
}): string {
  const responses = input.responses ?? {};
  const lines: string[] = [];
  const questions = input.questions ?? [];
  const seen = new Set<string>();

  const write = (question: FormsAnswerQuestion | undefined, id: string, value: unknown) => {
    if (value == null || value === '') return;
    const prompt = question?.text_da || question?.text || id;
    const rendered = renderAnswer(value, question);
    if (!rendered) return;
    lines.push(`${prompt}\n${rendered}`);
    seen.add(id);
  };

  for (const question of questions) {
    write(question, question.id, responses[question.id]);
  }
  for (const [id, value] of Object.entries(responses)) {
    if (seen.has(id)) continue;
    write(undefined, id, value);
  }
  return lines.join('\n\n');
}

function renderAnswer(value: unknown, question: FormsAnswerQuestion | undefined): string {
  if (Array.isArray(value)) {
    return value
      .map((item) => renderAnswer(item, question))
      .filter(Boolean)
      .join(', ');
  }
  if (typeof value === 'object' && value) {
    const recordValue = value as Record<string, unknown>;
    if (typeof recordValue.label === 'string') return recordValue.label;
    if (typeof recordValue.value === 'string') return optionLabel(recordValue.value, question);
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return optionLabel(String(value), question);
  }
  return '';
}

function optionLabel(value: string, question: FormsAnswerQuestion | undefined): string {
  const option = question?.options?.find((item) => item.value === value);
  return option?.label_da || option?.label || value;
}

export function finishedPdfObjectPath(
  clinicId: string,
  patientId: string,
  reportId: string,
): string {
  return `${clinicId}/${patientId}/${reportId}.pdf`;
}

export function letterheadObjectPath(clinicId: string, filename: string): string {
  const safe = filename.replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '') || 'brevpapir';
  return `${clinicId}/letterhead/${Date.now()}-${safe}`;
}
